import { useEffect, useState } from 'react';
import { BehaviorSubject } from 'rxjs';
import { AdviseCustomer } from '../types/advise';
import {
  fetchAdviseCustomers as apiFetchAdviseCustomers,
  findOrCreateAdviseCustomer as apiFindOrCreateAdviseCustomer,
  createAdviseCustomer as apiCreateAdviseCustomer,
  updateAdviseCustomer as apiUpdateAdviseCustomer,
  deleteAdviseCustomer as apiDeleteAdviseCustomer
} from '../Api/Advise/Customers';
import { addError } from './Error';

export interface UseAdviseCustomersReturn {
  customers: AdviseCustomer[];
  activeCustomer: AdviseCustomer | null;
  fetchCustomers: (forceRefresh?: boolean) => Promise<void>;
  ensureCustomerForAccount: (accountId: string, accountName: string) => Promise<AdviseCustomer | null>;
  createCustomer: (customer: Partial<AdviseCustomer>) => Promise<AdviseCustomer | null>;
  updateCustomer: (id: string, customer: Partial<AdviseCustomer>) => Promise<AdviseCustomer | null>;
  deleteCustomer: (id: string) => Promise<boolean>;
  clear: () => void;
}

const defaultData: AdviseCustomer[] = [];
const subject = new BehaviorSubject<AdviseCustomer[]>(defaultData);
const activeCustomerSubject = new BehaviorSubject<AdviseCustomer | null>(null);
const loadingSubject = new BehaviorSubject<boolean>(false);
const emitErrorState = (message?: string): void => addError('advise-customer', message);

const upsertCustomer = (customer: AdviseCustomer): void => {
  const current = subject.getValue();
  const exists = current.some((c) => c.id === customer.id);
  subject.next(exists ? current.map((c) => (c.id === customer.id ? customer : c)) : [...current, customer]);
};

export const getAdviseCustomerSubject = (): BehaviorSubject<AdviseCustomer[]> => subject;
export const getAdviseCustomers = (): AdviseCustomer[] => subject.value;
export const getAdviseActiveCustomerSubject = (): BehaviorSubject<AdviseCustomer | null> => activeCustomerSubject;
export const getAdviseActiveCustomer = (): AdviseCustomer | null => activeCustomerSubject.value;
export const getAdviseCustomerLoadingSubject = (): BehaviorSubject<boolean> => loadingSubject;

export const useAdviseCustomers = (): UseAdviseCustomersReturn => {
  const [customers, setCustomers] = useState(subject.getValue());
  const [activeCustomer, setActiveCustomer] = useState(activeCustomerSubject.getValue());

  useEffect(() => {
    const sub = subject.subscribe(setCustomers);
    const activeSub = activeCustomerSubject.subscribe(setActiveCustomer);
    return () => {
      sub.unsubscribe();
      activeSub.unsubscribe();
    };
  }, []);

  const fetchCustomers = async (forceRefresh = false): Promise<void> => {
    if (loadingSubject.getValue()) return;
    if (subject.getValue().length > 0 && !forceRefresh) return;
    loadingSubject.next(true);

    const { data, error } = await apiFetchAdviseCustomers();
    if (data) {
      emitErrorState();
      subject.next(data);
      setCustomers(data);
    }
    if (error) {
      subject.next(defaultData);
      emitErrorState(error.message);
    }
    loadingSubject.next(false);
  };

  const ensureCustomerForAccount = async (accountId: string, accountName: string): Promise<AdviseCustomer | null> => {
    if (loadingSubject.getValue()) return activeCustomerSubject.getValue();
    loadingSubject.next(true);
    const { data, error } = await apiFindOrCreateAdviseCustomer(accountId, accountName);
    if (data) {
      emitErrorState();
      upsertCustomer(data);
      activeCustomerSubject.next(data);
      loadingSubject.next(false);
      return data;
    }
    if (error) emitErrorState(error.message);
    loadingSubject.next(false);
    return null;
  };

  const createCustomer = async (customer: Partial<AdviseCustomer>): Promise<AdviseCustomer | null> => {
    const { data, error } = await apiCreateAdviseCustomer(customer);
    if (data) {
      emitErrorState();
      const updated = [...subject.getValue(), data];
      subject.next(updated);
      setCustomers(updated);
      return data;
    }
    if (error) emitErrorState(error.message);
    return null;
  };

  const updateCustomer = async (id: string, customer: Partial<AdviseCustomer>): Promise<AdviseCustomer | null> => {
    const { data, error } = await apiUpdateAdviseCustomer(id, customer);
    if (data) {
      emitErrorState();
      const updated = subject.getValue().map((c) => (c.id === id ? data : c));
      subject.next(updated);
      if (activeCustomerSubject.getValue()?.id === id) activeCustomerSubject.next(data);
      return data;
    }
    if (error) emitErrorState(error.message);
    return null;
  };

  const deleteCustomer = async (id: string): Promise<boolean> => {
    const { error } = await apiDeleteAdviseCustomer(id);
    if (!error) {
      emitErrorState();
      const updated = subject.getValue().filter((c) => c.id !== id);
      subject.next(updated);
      if (activeCustomerSubject.getValue()?.id === id) activeCustomerSubject.next(null);
      return true;
    }
    emitErrorState(error.message);
    return false;
  };

  const clear = () => {
    subject.next(defaultData);
    activeCustomerSubject.next(null);
  };

  return {
    customers,
    activeCustomer,
    fetchCustomers,
    ensureCustomerForAccount,
    createCustomer,
    updateCustomer,
    deleteCustomer,
    clear
  };
};
